'use client';
// ============================================================
// DESIGN EDITOR — DEV ONLY
// To remove: delete this file and the <DesignEditor /> line in layout.tsx
// ============================================================
import React, { useState, useEffect, useRef, useCallback } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PositionedEdit {
  selector: string;
  label: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fontSize?: number;
  color?: string;
  backgroundColor?: string;
  textContent?: string;
  customCSS?: string;
}

interface FeedbackItem {
  id: string;
  selector: string;
  label: string;
  note: string;
  timestamp: string;
  elementInfo?: {
    tag: string;
    id: string;
    classes: string;
    text: string;
    page: string;
    position: string;
    componentName?: string;
    filePath?: string;
  };
}

interface DesignEditorStore {
  edits: Record<string, PositionedEdit>;
  feedback: FeedbackItem[];
  version: number;
}

const STORAGE_KEY = 'DesignEditor_v2';

function loadStore(): DesignEditorStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { edits: {}, feedback: [], version: 2 };
}

function saveStore(store: DesignEditorStore) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {}
}

// ─── Page → Component/File mapping ───────────────────────────────────────────

const PAGE_COMPONENT_MAP: Record<string, { component: string; file: string }[]> = {
  '/': [{ component: 'Home page', file: 'src/app/page.tsx' }],
  '/login': [{ component: 'Login page', file: 'src/app/login/page.tsx' }],
  '/character-creation-screen': [
    { component: 'CharacterCreationWizard', file: 'src/app/character-creation-screen/components/CharacterCreationWizard.tsx' },
    { component: 'IntroWalkthrough', file: 'src/app/character-creation-screen/components/IntroWalkthrough.tsx' },
    { component: 'AvatarPreviewPanel', file: 'src/app/character-creation-screen/components/AvatarPreviewPanel.tsx' },
    { component: 'StepGender', file: 'src/app/character-creation-screen/components/StepGender.tsx' },
    { component: 'StepOrientation', file: 'src/app/character-creation-screen/components/StepOrientation.tsx' },
    { component: 'StepCity', file: 'src/app/character-creation-screen/components/StepCity.tsx' },
    { component: 'StepLifeProfile', file: 'src/app/character-creation-screen/components/StepLifeProfile.tsx' },
    { component: 'StepAvatar', file: 'src/app/character-creation-screen/components/StepAvatar.tsx' },
    { component: 'StepConfirm', file: 'src/app/character-creation-screen/components/StepConfirm.tsx' },
  ],
  '/check-in-screen': [
    { component: 'CheckInScreen', file: 'src/app/check-in-screen/components/CheckInScreen.tsx' },
    { component: 'LocationSelector', file: 'src/app/check-in-screen/components/LocationSelector.tsx' },
    { component: 'NearbyPlayers', file: 'src/app/check-in-screen/components/NearbyPlayers.tsx' },
    { component: 'LocationHistory', file: 'src/app/check-in-screen/components/LocationHistory.tsx' },
    { component: 'DailySummaryCard', file: 'src/app/check-in-screen/components/DailySummaryCard.tsx' },
    { component: 'VisibilitySettings', file: 'src/app/check-in-screen/components/VisibilitySettings.tsx' },
    { component: 'Sidebar', file: 'src/components/Sidebar.tsx' },
  ],
  '/city-map-screen': [
    { component: 'CityMapScreen', file: 'src/app/city-map-screen/components/CityMapScreen.tsx' },
    { component: 'InteractiveMap', file: 'src/app/city-map-screen/components/InteractiveMap.tsx' },
    { component: 'WeatherWidget', file: 'src/app/city-map-screen/components/WeatherWidget.tsx' },
    { component: 'CityZoneGrid', file: 'src/app/city-map-screen/components/CityZoneGrid.tsx' },
    { component: 'DailyPlanPanel', file: 'src/app/city-map-screen/components/DailyPlanPanel.tsx' },
    { component: 'ActiveEventsBanner', file: 'src/app/city-map-screen/components/ActiveEventsBanner.tsx' },
    { component: 'TransportCalculator', file: 'src/app/city-map-screen/components/TransportCalculator.tsx' },
    { component: 'Sidebar', file: 'src/components/Sidebar.tsx' },
  ],
  '/chat-screen': [
    { component: 'ChatScreen', file: 'src/app/chat-screen/components/ChatScreen.tsx' },
    { component: 'ChatArea', file: 'src/app/chat-screen/components/ChatArea.tsx' },
    { component: 'ConversationSidebar', file: 'src/app/chat-screen/components/ConversationSidebar.tsx' },
    { component: 'AIAdvisorPanel', file: 'src/app/chat-screen/components/AIAdvisorPanel.tsx' },
    { component: 'NPCDatingSimulator', file: 'src/app/chat-screen/components/NPCDatingSimulator.tsx' },
    { component: 'Sidebar', file: 'src/components/Sidebar.tsx' },
  ],
  '/discovery-screen': [
    { component: 'DiscoveryScreen', file: 'src/app/discovery-screen/components/DiscoveryScreen.tsx' },
    { component: 'DiscoveryFilters', file: 'src/app/discovery-screen/components/DiscoveryFilters.tsx' },
    { component: 'PlayerCard', file: 'src/app/discovery-screen/components/PlayerCard.tsx' },
    { component: 'Sidebar', file: 'src/components/Sidebar.tsx' },
  ],
  '/profile-view-screen': [
    { component: 'ProfileViewScreen', file: 'src/app/profile-view-screen/components/ProfileViewScreen.tsx' },
    { component: 'ProfileHeader', file: 'src/app/profile-view-screen/components/ProfileHeader.tsx' },
    { component: 'TraitsBadges', file: 'src/app/profile-view-screen/components/TraitsBadges.tsx' },
    { component: 'ProfileActions', file: 'src/app/profile-view-screen/components/ProfileActions.tsx' },
    { component: 'MemoryTimeline', file: 'src/app/profile-view-screen/components/MemoryTimeline.tsx' },
    { component: 'AttractionLayers', file: 'src/app/profile-view-screen/components/AttractionLayers.tsx' },
    { component: 'Sidebar', file: 'src/components/Sidebar.tsx' },
  ],
  '/challenges-screen': [
    { component: 'ChallengesScreen', file: 'src/app/challenges-screen/components/ChallengesScreen.tsx' },
    { component: 'QuestBoard', file: 'src/app/challenges-screen/components/QuestBoard.tsx' },
    { component: 'RewardsPanel', file: 'src/app/challenges-screen/components/RewardsPanel.tsx' },
    { component: 'StreakPanel', file: 'src/app/challenges-screen/components/StreakPanel.tsx' },
    { component: 'LeaderboardPanel', file: 'src/app/challenges-screen/components/LeaderboardPanel.tsx' },
    { component: 'Sidebar', file: 'src/components/Sidebar.tsx' },
  ],
  '/achievements-screen': [
    { component: 'AchievementsScreen', file: 'src/app/achievements-screen/components/AchievementsScreen.tsx' },
    { component: 'Sidebar', file: 'src/components/Sidebar.tsx' },
  ],
  '/dating-history-screen': [
    { component: 'DatingHistoryScreen', file: 'src/app/dating-history-screen/components/DatingHistoryScreen.tsx' },
    { component: 'DateTimelineCard', file: 'src/app/dating-history-screen/components/DateTimelineCard.tsx' },
    { component: 'DatingStatsPanel', file: 'src/app/dating-history-screen/components/DatingStatsPanel.tsx' },
    { component: 'Sidebar', file: 'src/components/Sidebar.tsx' },
  ],
  '/location-object-screen': [
    { component: 'LocationObjectScreen', file: 'src/app/location-object-screen/components/LocationObjectScreen.tsx' },
    { component: 'LocationHero', file: 'src/app/location-object-screen/components/LocationHero.tsx' },
    { component: 'AtmosphereMeters', file: 'src/app/location-object-screen/components/AtmosphereMeters.tsx' },
    { component: 'PeopleList', file: 'src/app/location-object-screen/components/PeopleList.tsx' },
    { component: 'ScenariosList', file: 'src/app/location-object-screen/components/ScenariosList.tsx' },
    { component: 'AIScenarioGenerator', file: 'src/app/location-object-screen/components/AIScenarioGenerator.tsx' },
    { component: 'Sidebar', file: 'src/components/Sidebar.tsx' },
  ],
  '/settings-screen': [
    { component: 'SettingsScreen', file: 'src/app/settings-screen/components/SettingsScreen.tsx' },
    { component: 'Sidebar', file: 'src/components/Sidebar.tsx' },
  ],
  '/notifications-hub': [
    { component: 'NotificationsHub', file: 'src/app/notifications-hub/components/NotificationsHub.tsx' },
    { component: 'Sidebar', file: 'src/components/Sidebar.tsx' },
  ],
  '/store': [
    { component: 'CosmeticsStore', file: 'src/app/store/components/CosmeticsStore.tsx' },
    { component: 'Sidebar', file: 'src/components/Sidebar.tsx' },
  ],
  '/daily-login': [
    { component: 'DailyLoginScreen', file: 'src/app/daily-login/components/DailyLoginScreen.tsx' },
  ],
  '/direct-messages': [
    { component: 'DirectMessagesScreen', file: 'src/app/direct-messages/components/DirectMessagesScreen.tsx' },
    { component: 'Sidebar', file: 'src/components/Sidebar.tsx' },
  ],
  '/seasonal-events': [
    { component: 'SeasonalEventsScreen', file: 'src/app/seasonal-events/components/SeasonalEventsScreen.tsx' },
    { component: 'Sidebar', file: 'src/components/Sidebar.tsx' },
  ],
};

function getPageComponents(page: string): { component: string; file: string }[] {
  return PAGE_COMPONENT_MAP[page] || [{ component: 'Unknown component', file: `src/app${page}/page.tsx` }];
}

// ─── Build a stable CSS selector ─────────────────────────────────────────────

function buildSelector(el: HTMLElement): string {
  const parts: string[] = [];
  let current: HTMLElement | null = el;
  while (current && current !== document.body) {
    let part = current.tagName.toLowerCase();
    if (current.id) {
      part += `#${current.id}`;
      parts.unshift(part);
      break;
    }
    const siblings = current.parentElement
      ? Array.from(current.parentElement.children).filter(c => c.tagName === current!.tagName)
      : [];
    if (siblings.length > 1) {
      const idx = siblings.indexOf(current) + 1;
      part += `:nth-of-type(${idx})`;
    }
    parts.unshift(part);
    current = current.parentElement as HTMLElement | null;
  }
  return parts.join(' > ');
}

function getLabel(el: HTMLElement): string {
  const tag = el.tagName.toLowerCase();
  const text = el.textContent?.trim().slice(0, 40);
  if (text) return `<${tag}> "${text}"`;
  if (el.id) return `#${el.id}`;
  return `<${tag}>`;
}

// ─── Apply stored edits to DOM ────────────────────────────────────────────────

function applyEditToElement(el: HTMLElement, edit: PositionedEdit) {
  if (edit.x !== undefined) { el.style.position = 'relative'; el.style.left = `${edit.x}px`; }
  if (edit.y !== undefined) { el.style.position = 'relative'; el.style.top = `${edit.y}px`; }
  if (edit.width !== undefined) el.style.width = `${edit.width}px`;
  if (edit.height !== undefined) el.style.height = `${edit.height}px`;
  if (edit.fontSize !== undefined) el.style.fontSize = `${edit.fontSize}px`;
  if (edit.color) el.style.color = edit.color;
  if (edit.backgroundColor) el.style.backgroundColor = edit.backgroundColor;
  if (edit.textContent !== undefined && el.childElementCount === 0) el.textContent = edit.textContent;
  if (edit.customCSS) {
    edit.customCSS.split(';').forEach(rule => {
      const [prop, val] = rule.split(':');
      if (prop && val) {
        try { (el.style as unknown as Record<string, string>)[prop.trim()] = val.trim(); } catch {}
      }
    });
  }
}

export function applyDesignEdits() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const store: DesignEditorStore = JSON.parse(raw);
    Object.values(store.edits).forEach(edit => {
      try {
        const el = document.querySelector(edit.selector) as HTMLElement | null;
        if (el) applyEditToElement(el, edit);
      } catch {}
    });
  } catch {}
}

// ─── Overlay: selection box + resize handles ─────────────────────────────────

type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se';

interface SelectionOverlayProps {
  targetEl: HTMLElement;
  onDragMove: (dx: number, dy: number) => void;
  onResizeMove: (handle: ResizeHandle, dx: number, dy: number) => void;
  onDragEnd: () => void;
  onResizeEnd: () => void;
  onDoubleClick: () => void;
}

function SelectionOverlay({ targetEl, onDragMove, onResizeMove, onDragEnd, onResizeEnd, onDoubleClick }: SelectionOverlayProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const activeHandle = useRef<ResizeHandle | null>(null);
  const lastPos = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);

  const updateRect = useCallback(() => {
    setRect(targetEl.getBoundingClientRect());
  }, [targetEl]);

  useEffect(() => {
    updateRect();
    const ro = new ResizeObserver(updateRect);
    ro.observe(targetEl);
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [targetEl, updateRect]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      let dx = e.clientX - lastPos.current.x;
      let dy = e.clientY - lastPos.current.y;
      lastPos.current = { x: e.clientX, y: e.clientY };
      if (activeHandle.current) {
        onResizeMove(activeHandle.current, dx, dy);
        updateRect();
      } else if (isDragging.current) {
        onDragMove(dx, dy);
        updateRect();
      }
    };
    const onUp = () => {
      if (activeHandle.current) { activeHandle.current = null; onResizeEnd(); }
      else if (isDragging.current) { isDragging.current = false; onDragEnd(); }
      updateRect();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [onDragMove, onResizeMove, onDragEnd, onResizeEnd, updateRect]);

  if (!rect) return null;

  // Minimum visible/interactive size for small elements
  const MIN_SIZE = 20;
  const displayW = Math.max(rect.width, MIN_SIZE);
  const displayH = Math.max(rect.height, MIN_SIZE);
  // Center the overlay on the element when expanded
  const offsetLeft = rect.left - (displayW - rect.width) / 2;
  const offsetTop = rect.top - (displayH - rect.height) / 2;

  const handles: { handle: ResizeHandle; style: React.CSSProperties }[] = [
    { handle: 'nw', style: { top: -5, left: -5, cursor: 'nw-resize' } },
    { handle: 'n',  style: { top: -5, left: '50%', transform: 'translateX(-50%)', cursor: 'n-resize' } },
    { handle: 'ne', style: { top: -5, right: -5, cursor: 'ne-resize' } },
    { handle: 'e',  style: { top: '50%', right: -5, transform: 'translateY(-50%)', cursor: 'e-resize' } },
    { handle: 'se', style: { bottom: -5, right: -5, cursor: 'se-resize' } },
    { handle: 's',  style: { bottom: -5, left: '50%', transform: 'translateX(-50%)', cursor: 's-resize' } },
    { handle: 'sw', style: { bottom: -5, left: -5, cursor: 'sw-resize' } },
    { handle: 'w',  style: { top: '50%', left: -5, transform: 'translateY(-50%)', cursor: 'w-resize' } },
  ];

  return (
    <div
      data-de-panel="true"
      style={{
        position: 'fixed',
        left: offsetLeft,
        top: offsetTop,
        width: displayW,
        height: displayH,
        zIndex: 2147483640,
        border: '2px solid #7c3aed',
        boxSizing: 'border-box',
        pointerEvents: 'none',
      }}
    >
      {/* Drag area — expanded hit area for small elements */}
      <div
        data-de-panel="true"
        title="Drag to move"
        onMouseDown={e => {
          e.preventDefault();
          e.stopPropagation();
          isDragging.current = true;
          lastPos.current = { x: e.clientX, y: e.clientY };
        }}
        onDoubleClick={e => { e.preventDefault(); e.stopPropagation(); onDoubleClick(); }}
        style={{
          position: 'absolute', inset: 0,
          cursor: 'move', pointerEvents: 'all',
          background: 'rgba(124,58,237,0.05)',
        }}
      />
      {/* Resize handles */}
      {handles.map(({ handle, style }) => (
        <div
          key={handle}
          data-de-panel="true"
          onMouseDown={e => {
            e.preventDefault();
            e.stopPropagation();
            activeHandle.current = handle;
            lastPos.current = { x: e.clientX, y: e.clientY };
          }}
          style={{
            position: 'absolute',
            width: 10, height: 10,
            background: '#7c3aed',
            border: '2px solid #fff',
            borderRadius: 2,
            pointerEvents: 'all',
            zIndex: 1,
            ...style,
          }}
        />
      ))}
    </div>
  );
}

// ─── Inline text editor ───────────────────────────────────────────────────────

interface InlineTextEditorProps {
  targetEl: HTMLElement;
  initialText: string;
  onSave: (text: string) => void;
  onCancel: () => void;
}

function InlineTextEditor({ targetEl, initialText, onSave, onCancel }: InlineTextEditorProps) {
  const [text, setText] = useState(initialText);
  const rect = targetEl.getBoundingClientRect();

  return (
    <div data-de-panel="true" style={{
      position: 'fixed',
      left: rect.left,
      top: rect.top,
      width: Math.max(rect.width, 200),
      zIndex: 2147483641,
    }}>
      <textarea
        autoFocus
        value={text}
        onChange={e => setText(e.target.value)}
        style={{
          width: '100%',
          minHeight: Math.max(rect.height, 40),
          background: '#1a1a2e',
          color: '#e2e8f0',
          border: '2px solid #db2777',
          borderRadius: 4,
          padding: '4px 8px',
          fontSize: 13,
          resize: 'both',
          outline: 'none',
          fontFamily: 'inherit',
          boxSizing: 'border-box',
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSave(text); }
          if (e.key === 'Escape') onCancel();
        }}
      />
      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <button data-de-panel="true" onClick={() => onSave(text)} style={btnStyle('#7c3aed')}>✓ Запази</button>
        <button data-de-panel="true" onClick={onCancel} style={btnStyle('#6b7280')}>✕ Отказ</button>
        <span style={{ color: '#6b7280', fontSize: 10, alignSelf: 'center' }}>Enter = запази, Esc = отказ</span>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  background: '#0f0f1a', border: '1px solid #2d2d4e', borderRadius: 4,
  color: '#e2e8f0', padding: '4px 8px', fontSize: 12, outline: 'none',
  width: '100%', boxSizing: 'border-box',
};

function btnStyle(bg: string): React.CSSProperties {
  return {
    background: bg, border: 'none', borderRadius: 4, color: '#fff',
    padding: '3px 10px', cursor: 'pointer', fontSize: 11,
  };
}

// ─── Inspector Panel ─────────────────────────────────────────────────────────

interface InspectorProps {
  edit: PositionedEdit;
  onChange: (patch: Partial<PositionedEdit>) => void;
  onClose: () => void;
  onDelete: () => void;
  onEditText: () => void;
}

function useDraggablePanel(initialX: number, initialY: number) {
  const [pos, setPos] = useState({ x: initialX, y: initialY });
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    offset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  }, [pos]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      setPos({ x: e.clientX - offset.current.x, y: e.clientY - offset.current.y });
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  return { pos, onMouseDown };
}

function Inspector({ edit, onChange, onClose, onDelete, onEditText }: InspectorProps) {
  const { pos, onMouseDown } = useDraggablePanel(
    typeof window !== 'undefined' ? window.innerWidth - 360 : 800,
    80
  );

  return (
    <div data-de-panel="true" style={{
      position: 'fixed', left: pos.x, top: pos.y, zIndex: 2147483647,
      width: 340, background: '#1a1a2e', border: '1px solid #7c3aed',
      borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
      fontFamily: 'system-ui, sans-serif', fontSize: 12, color: '#e2e8f0',
      userSelect: 'none',
    }}>
      <div onMouseDown={onMouseDown} style={{
        background: 'linear-gradient(90deg,#7c3aed,#db2777)',
        borderRadius: '9px 9px 0 0', padding: '8px 12px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        cursor: 'grab',
      }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>✏️ Design Editor — Inspector</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button data-de-panel="true" onClick={onClose} style={btnStyle('#6b7280')}>✕</button>
        </div>
      </div>

      <div style={{ padding: '6px 12px', background: '#0f0f1a', fontSize: 11, color: '#a78bfa', borderBottom: '1px solid #2d2d4e' }}>
        {edit.label}
      </div>

      <div style={{ padding: '10px 12px', maxHeight: 460, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Delete element button — prominent, at the top */}
        <button
          data-de-panel="true"
          onClick={onDelete}
          style={{
            background: 'linear-gradient(135deg,#dc2626,#ef4444)',
            border: '2px solid #ef4444',
            borderRadius: 8,
            color: '#fff',
            padding: '10px 12px',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 700,
            textAlign: 'center',
            letterSpacing: '0.02em',
          }}
        >
          🗑️ Изтрий елемент (Delete)
        </button>

        {/* Text */}
        <button data-de-panel="true" onClick={onEditText} style={{
          background: '#2d2d4e', border: '1px solid #7c3aed', borderRadius: 6,
          color: '#e2e8f0', padding: '8px 12px', cursor: 'pointer', fontSize: 12,
          textAlign: 'left',
        }}>
          📝 Редактирай текст (двоен клик върху елемента)
        </button>

        <Row label="Размер на шрифт (px)">
          <NumInput value={edit.fontSize} onChange={v => onChange({ fontSize: v })} min={6} max={200} />
        </Row>
        <Row label="Цвят на текст">
          <ColorInput value={edit.color || ''} onChange={v => onChange({ color: v })} />
        </Row>
        <Row label="Фон (background)">
          <ColorInput value={edit.backgroundColor || ''} onChange={v => onChange({ backgroundColor: v })} />
        </Row>
        <Row label="Ширина (px)">
          <NumInput value={edit.width} onChange={v => onChange({ width: v })} min={0} max={2000} />
        </Row>
        <Row label="Височина (px)">
          <NumInput value={edit.height} onChange={v => onChange({ height: v })} min={0} max={2000} />
        </Row>
        <Row label="X позиция (px)">
          <NumInput value={edit.x} onChange={v => onChange({ x: v })} min={-2000} max={2000} />
        </Row>
        <Row label="Y позиция (px)">
          <NumInput value={edit.y} onChange={v => onChange({ y: v })} min={-2000} max={2000} />
        </Row>
        <Row label="Допълнителен CSS">
          <input
            data-de-panel="true"
            type="text"
            value={edit.customCSS || ''}
            onChange={e => onChange({ customCSS: e.target.value })}
            placeholder="prop:val; prop:val"
            style={inputStyle}
          />
        </Row>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <label style={{ color: '#94a3b8', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

function NumInput({ value, onChange, min, max }: { value?: number; onChange: (v: number) => void; min: number; max: number }) {
  return (
    <div data-de-panel="true" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input
        data-de-panel="true"
        type="range" min={min} max={max}
        value={value ?? 0}
        onChange={e => onChange(Number(e.target.value))}
        style={{ flex: 1, accentColor: '#7c3aed' }}
      />
      <input
        data-de-panel="true"
        type="number" min={min} max={max}
        value={value ?? ''}
        onChange={e => onChange(Number(e.target.value))}
        style={{ ...inputStyle, width: 60 }}
      />
    </div>
  );
}

function ColorInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div data-de-panel="true" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input
        data-de-panel="true"
        type="color"
        value={value || '#ffffff'}
        onChange={e => onChange(e.target.value)}
        style={{ width: 36, height: 28, border: 'none', background: 'none', cursor: 'pointer' }}
      />
      <input
        data-de-panel="true"
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="#rrggbb или rgba(...)"
        style={{ ...inputStyle, flex: 1 }}
      />
    </div>
  );
}

// ─── Layer Hierarchy Panel ────────────────────────────────────────────────────

interface LayerHierarchyPanelProps {
  edits: Record<string, PositionedEdit>;
  selectedId: string | null;
  onSelectLayer: (selector: string) => void;
  onDeleteLayer: (selector: string) => void;
  onClose: () => void;
}

function LayerHierarchyPanel({ edits, selectedId, onSelectLayer, onDeleteLayer, onClose }: LayerHierarchyPanelProps) {
  const { pos, onMouseDown } = useDraggablePanel(
    typeof window !== 'undefined' ? window.innerWidth - 420 : 900,
    200
  );
  const items = Object.entries(edits);

  const tagColor = (label: string): string => {
    if (label.startsWith('<div')) return '#60a5fa';
    if (label.startsWith('<p')) return '#34d399';
    if (label.startsWith('<h')) return '#f472b6';
    if (label.startsWith('<span')) return '#fbbf24';
    if (label.startsWith('<button')) return '#a78bfa';
    if (label.startsWith('<img')) return '#fb923c';
    if (label.startsWith('<section') || label.startsWith('<main') || label.startsWith('<article')) return '#22d3ee';
    if (label.startsWith('#')) return '#e879f9';
    return '#94a3b8';
  };

  const tagIcon = (label: string): string => {
    if (label.startsWith('<div')) return '▣';
    if (label.startsWith('<p')) return '¶';
    if (label.startsWith('<h')) return 'H';
    if (label.startsWith('<span')) return '⟨⟩';
    if (label.startsWith('<button')) return '⬡';
    if (label.startsWith('<img')) return '🖼';
    if (label.startsWith('<section') || label.startsWith('<main') || label.startsWith('<article')) return '⬜';
    if (label.startsWith('#')) return '#';
    return '◈';
  };

  return (
    <div data-de-panel="true" style={{
      position: 'fixed', left: pos.x, top: pos.y, zIndex: 2147483646,
      width: 300, background: '#1a1a2e', border: '1px solid #0ea5e9',
      borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
      fontFamily: 'system-ui, sans-serif', fontSize: 12, color: '#e2e8f0',
      userSelect: 'none',
    }}>
      <div onMouseDown={onMouseDown} style={{
        background: 'linear-gradient(90deg,#0284c7,#0ea5e9)',
        borderRadius: '9px 9px 0 0', padding: '8px 12px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        cursor: 'grab',
      }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>🗂 Слоеве ({items.length})</span>
        <button data-de-panel="true" onClick={onClose} style={btnStyle('#6b7280')}>✕</button>
      </div>

      <div style={{ padding: '6px 8px', background: '#0f0f1a', borderBottom: '1px solid #1e293b', fontSize: 10, color: '#64748b' }}>
        Клик = избери · Del = изтрий слой
      </div>

      <div style={{ maxHeight: 400, overflowY: 'auto', padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 3 }}>
        {items.length === 0 && (
          <div style={{ color: '#6b5563', textAlign: 'center', padding: '20px 0', fontSize: 11 }}>
            Няма редактирани елементи.<br />
            <span style={{ fontSize: 10 }}>Избери елемент в Design режим.</span>
          </div>
        )}
        {items.map(([id, edit]) => {
          const isSelected = id === selectedId;
          const color = tagColor(edit.label);
          const icon = tagIcon(edit.label);
          const hasPos = edit.x !== undefined || edit.y !== undefined;
          const hasSize = edit.width !== undefined || edit.height !== undefined;
          const hasStyle = edit.fontSize !== undefined || edit.color || edit.backgroundColor;

          return (
            <div
              key={id}
              data-de-panel="true"
              onClick={() => onSelectLayer(id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
                background: isSelected ? 'rgba(14,165,233,0.15)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${isSelected ? '#0ea5e9' : '#1e293b'}`,
                transition: 'background 0.1s',
              }}
            >
              {/* Tag icon */}
              <span style={{
                width: 20, height: 20, borderRadius: 4,
                background: `${color}22`, border: `1px solid ${color}55`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, color, flexShrink: 0, fontWeight: 700,
              }}>{icon}</span>

              {/* Label */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  color: isSelected ? '#e2e8f0' : '#94a3b8',
                  fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontWeight: isSelected ? 700 : 400,
                }}>
                  {edit.label}
                </div>
                <div style={{ display: 'flex', gap: 4, marginTop: 2, flexWrap: 'wrap' }}>
                  {hasPos && <span style={{ fontSize: 9, color: '#60a5fa', background: 'rgba(96,165,250,0.1)', padding: '1px 4px', borderRadius: 3 }}>pos</span>}
                  {hasSize && <span style={{ fontSize: 9, color: '#34d399', background: 'rgba(52,211,153,0.1)', padding: '1px 4px', borderRadius: 3 }}>size</span>}
                  {hasStyle && <span style={{ fontSize: 9, color: '#f472b6', background: 'rgba(244,114,182,0.1)', padding: '1px 4px', borderRadius: 3 }}>style</span>}
                  {edit.textContent !== undefined && <span style={{ fontSize: 9, color: '#fbbf24', background: 'rgba(251,191,36,0.1)', padding: '1px 4px', borderRadius: 3 }}>text</span>}
                </div>
              </div>

              {/* Delete */}
              <button
                data-de-panel="true"
                onClick={e => { e.stopPropagation(); onDeleteLayer(id); }}
                style={{
                  background: 'transparent', border: 'none', color: '#4b5563',
                  cursor: 'pointer', fontSize: 12, padding: '2px 4px', borderRadius: 3,
                  flexShrink: 0,
                }}
                title="Изтрий слой"
              >✕</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Quick Style Presets Panel ────────────────────────────────────────────────

interface StylePreset {
  name: string;
  icon: string;
  color: string;
  patch: Partial<PositionedEdit>;
}

const STYLE_PRESETS: StylePreset[] = [
  { name: 'Hero Title', icon: 'H1', color: '#f472b6', patch: { fontSize: 48, color: '#ffffff', customCSS: 'font-weight:800;letter-spacing:-0.02em;line-height:1.1' } },
  { name: 'Section Title', icon: 'H2', color: '#e879f9', patch: { fontSize: 32, color: '#f1f5f9', customCSS: 'font-weight:700;letter-spacing:-0.01em' } },
  { name: 'Body Text', icon: '¶', color: '#94a3b8', patch: { fontSize: 16, color: '#cbd5e1', customCSS: 'line-height:1.6;font-weight:400' } },
  { name: 'Small Caption', icon: 'sm', color: '#64748b', patch: { fontSize: 12, color: '#94a3b8', customCSS: 'letter-spacing:0.04em;text-transform:uppercase;font-weight:500' } },
  { name: 'Primary Button', icon: '⬡', color: '#7c3aed', patch: { backgroundColor: '#7c3aed', color: '#ffffff', customCSS: 'border-radius:8px;padding:10px 20px;font-weight:700;font-size:14px;cursor:pointer' } },
  { name: 'Danger Button', icon: '⬡', color: '#ef4444', patch: { backgroundColor: '#ef4444', color: '#ffffff', customCSS: 'border-radius:8px;padding:10px 20px;font-weight:700;font-size:14px;cursor:pointer' } },
  { name: 'Ghost Button', icon: '⬡', color: '#475569', patch: { backgroundColor: 'transparent', color: '#e2e8f0', customCSS: 'border-radius:8px;padding:10px 20px;font-weight:600;font-size:14px;border:1px solid #475569;cursor:pointer' } },
  { name: 'Card', icon: '▣', color: '#1e293b', patch: { backgroundColor: '#1e293b', customCSS: 'border-radius:12px;padding:20px;border:1px solid #334155;box-shadow:0 4px 16px rgba(0,0,0,0.4)' } },
  { name: 'Dark Card', icon: '▣', color: '#0f172a', patch: { backgroundColor: '#0f172a', customCSS: 'border-radius:12px;padding:20px;border:1px solid #1e293b;box-shadow:0 8px 32px rgba(0,0,0,0.6)' } },
  { name: 'Gradient Card', icon: '▣', color: '#7c3aed', patch: { customCSS: 'border-radius:12px;padding:20px;background:linear-gradient(135deg,#1e1b4b,#312e81);border:1px solid #4338ca' } },
  { name: 'Badge Purple', icon: '◉', color: '#7c3aed', patch: { backgroundColor: 'rgba(124,58,237,0.2)', color: '#a78bfa', customCSS: 'border-radius:20px;padding:4px 12px;font-size:12px;font-weight:600;border:1px solid rgba(124,58,237,0.4)' } },
  { name: 'Badge Green', icon: '◉', color: '#059669', patch: { backgroundColor: 'rgba(5,150,105,0.2)', color: '#34d399', customCSS: 'border-radius:20px;padding:4px 12px;font-size:12px;font-weight:600;border:1px solid rgba(5,150,105,0.4)' } },
  { name: 'Badge Red', icon: '◉', color: '#ef4444', patch: { backgroundColor: 'rgba(239,68,68,0.2)', color: '#f87171', customCSS: 'border-radius:20px;padding:4px 12px;font-size:12px;font-weight:600;border:1px solid rgba(239,68,68,0.4)' } },
  { name: 'Highlight', icon: '✦', color: '#fbbf24', patch: { backgroundColor: 'rgba(251,191,36,0.15)', color: '#fde68a', customCSS: 'border-left:3px solid #f59e0b;padding-left:12px' } },
  { name: 'Invisible', icon: '○', color: '#334155', patch: { backgroundColor: 'transparent', color: 'transparent', customCSS: 'opacity:0;pointer-events:none' } },
  { name: 'Full Width', icon: '↔', color: '#0ea5e9', patch: { width: undefined, customCSS: 'width:100%;display:block' } },
  { name: 'Center Block', icon: '⊡', color: '#6366f1', patch: { customCSS: 'margin-left:auto;margin-right:auto;display:block;text-align:center' } },
  { name: 'Flex Row', icon: '⇒', color: '#10b981', patch: { customCSS: 'display:flex;flex-direction:row;align-items:center;gap:12px' } },
  { name: 'Flex Col', icon: '⇓', color: '#14b8a6', patch: { customCSS: 'display:flex;flex-direction:column;gap:8px' } },
  { name: 'Reset Styles', icon: '↺', color: '#6b7280', patch: { fontSize: undefined, color: undefined, backgroundColor: undefined, customCSS: '', x: undefined, y: undefined, width: undefined, height: undefined } },
];

interface QuickStylePresetsPanelProps {
  selectedId: string | null;
  selectedEl: HTMLElement | null;
  onApplyPreset: (patch: Partial<PositionedEdit>) => void;
  onClose: () => void;
}

function QuickStylePresetsPanel({ selectedId, selectedEl, onApplyPreset, onClose }: QuickStylePresetsPanelProps) {
  const { pos, onMouseDown } = useDraggablePanel(
    typeof window !== 'undefined' ? window.innerWidth - 360 : 800,
    460
  );
  const [search, setSearch] = useState('');
  const [appliedPreset, setAppliedPreset] = useState<string | null>(null);

  const filtered = search.trim()
    ? STYLE_PRESETS.filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
    : STYLE_PRESETS;

  const handleApply = (preset: StylePreset) => {
    if (!selectedId) return;
    onApplyPreset(preset.patch);
    setAppliedPreset(preset.name);
    setTimeout(() => setAppliedPreset(null), 1200);
  };

  return (
    <div data-de-panel="true" style={{
      position: 'fixed', left: pos.x, top: pos.y, zIndex: 2147483646,
      width: 320, background: '#1a1a2e', border: '1px solid #10b981',
      borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
      fontFamily: 'system-ui, sans-serif', fontSize: 12, color: '#e2e8f0',
      userSelect: 'none',
    }}>
      <div onMouseDown={onMouseDown} style={{
        background: 'linear-gradient(90deg,#059669,#10b981)',
        borderRadius: '9px 9px 0 0', padding: '8px 12px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        cursor: 'grab',
      }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>⚡ Бързи стилове</span>
        <button data-de-panel="true" onClick={onClose} style={btnStyle('#6b7280')}>✕</button>
      </div>

      {/* Selected element info */}
      <div style={{ padding: '6px 10px', background: '#0f0f1a', borderBottom: '1px solid #1e293b', fontSize: 10, color: '#64748b' }}>
        {selectedId ? (
          <div style={{ color: '#10b981', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ✓ Избран: {selectedEl ? `<${selectedEl.tagName.toLowerCase()}>` : selectedId.slice(0, 40)}
          </div>
        ) : (
          <div style={{ color: '#ef4444', fontSize: 10 }}>⚠ Избери елемент в Design режим</div>
        )}
      </div>

      {/* Search */}
      <div style={{ padding: '6px 10px', borderBottom: '1px solid #1e293b' }}>
        <input
          data-de-panel="true"
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Търси стил..."
          style={{ ...inputStyle, fontSize: 11 }}
        />
      </div>

      {/* Presets grid */}
      <div style={{ padding: '8px 10px', maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {filtered.length === 0 && (
          <div style={{ color: '#4b5563', textAlign: 'center', padding: '16px 0', fontSize: 11 }}>Няма намерени стилове</div>
        )}
        {filtered.map(preset => {
          const isApplied = appliedPreset === preset.name;
          return (
            <button
              key={preset.name}
              data-de-panel="true"
              onClick={() => handleApply(preset)}
              disabled={!selectedId}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: isApplied ? `${preset.color}22` : 'rgba(255,255,255,0.02)',
                border: `1px solid ${isApplied ? preset.color : '#1e293b'}`,
                borderRadius: 6, padding: '7px 10px', cursor: selectedId ? 'pointer' : 'not-allowed',
                color: '#e2e8f0', textAlign: 'left', width: '100%',
                opacity: selectedId ? 1 : 0.5,
                transition: 'all 0.15s',
              }}
            >
              <span style={{
                width: 26, height: 26, borderRadius: 5, flexShrink: 0,
                background: `${preset.color}33`, border: `1px solid ${preset.color}66`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, color: preset.color, fontWeight: 800,
              }}>{preset.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: isApplied ? 700 : 500, color: isApplied ? preset.color : '#e2e8f0' }}>
                  {isApplied ? '✓ Приложено!' : preset.name}
                </div>
                <div style={{ fontSize: 9, color: '#4b5563', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
                  {Object.entries(preset.patch)
                    .filter(([, v]) => v !== undefined && v !== '')
                    .slice(0, 3)
                    .map(([k, v]) => `${k}:${String(v).slice(0, 12)}`)
                    .join(' · ')}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Saved Edits Panel ────────────────────────────────────────────────────────

interface SavedEditsPanelProps {
  edits: Record<string, PositionedEdit>;
  onDelete: (id: string) => void;
  onClearAll: () => void;
  onClose: () => void;
}

function SavedEditsPanel({ edits, onDelete, onClearAll, onClose }: SavedEditsPanelProps) {
  const { pos, onMouseDown } = useDraggablePanel(
    typeof window !== 'undefined' ? window.innerWidth - 420 : 900,
    80
  );
  const [copied, setCopied] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<{ success: boolean; message: string } | null>(null);
  const items = Object.entries(edits);

  const handleCopyAll = () => {
    if (items.length === 0) return;
    const text = items.map(([id, edit]) => {
      const parts: string[] = [`Елемент: ${edit.label}`, `Selector: ${edit.selector}`];
      if (edit.x !== undefined) parts.push(`X: ${edit.x}px`);
      if (edit.y !== undefined) parts.push(`Y: ${edit.y}px`);
      if (edit.width !== undefined) parts.push(`Ширина: ${edit.width}px`);
      if (edit.height !== undefined) parts.push(`Височина: ${edit.height}px`);
      if (edit.fontSize !== undefined) parts.push(`Размер шрифт: ${edit.fontSize}px`);
      if (edit.color) parts.push(`Цвят: ${edit.color}`);
      if (edit.backgroundColor) parts.push(`Фон: ${edit.backgroundColor}`);
      if (edit.textContent !== undefined) parts.push(`Текст: ${edit.textContent.slice(0, 30)}"`);
      if (edit.customCSS) parts.push(`CSS: ${edit.customCSS}`);
      return parts.join('\n');
    }).join('\n\n---\n\n');
    navigator.clipboard.writeText(`=== DESIGN EDITOR ПРОМЕНИ (${items.length}) ===\n\n${text}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleApplyToCode = async () => {
    if (items.length === 0) return;
    setApplying(true);
    setApplyResult(null);
    try {
      const res = await fetch('/api/design-editor/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ edits }),
      });
      const data = await res.json();
      setApplyResult({ success: data.success, message: data.message });
    } catch (err) {
      setApplyResult({ success: false, message: `Грешка при свързване с API: ${String(err)}` });
    } finally {
      setApplying(false);
    }
  };

  return (
    <div data-de-panel="true" style={{
      position: 'fixed', left: pos.x, top: pos.y, zIndex: 2147483646,
      width: 380, background: '#1a1a2e', border: '1px solid #7c3aed',
      borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
      fontFamily: 'system-ui, sans-serif', fontSize: 12, color: '#e2e8f0',
    }}>
      <div onMouseDown={onMouseDown} style={{
        background: 'linear-gradient(90deg,#7c3aed,#db2777)',
        borderRadius: '9px 9px 0 0', padding: '8px 12px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        cursor: 'grab',
      }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>✏️ Запазени Design промени ({items.length})</span>
        <button data-de-panel="true" onClick={onClose} style={btnStyle('#6b7280')}>✕</button>
      </div>
      <div style={{ padding: 10, maxHeight: 460, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.length === 0 && (
          <div style={{ color: '#6b7280', textAlign: 'center', padding: 20 }}>
            Няма запазени промени.<br />
            <span style={{ fontSize: 10 }}>Направи промени и натисни 💾 Save.</span>
          </div>
        )}
        {items.map(([id, edit]) => (
          <div key={id} style={{ background: '#0f0f1a', borderRadius: 6, padding: '8px 10px', border: '1px solid #2d2d4e' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
              <span style={{ color: '#a78bfa', fontSize: 10, fontWeight: 700, flex: 1 }}>{edit.label}</span>
              <button data-de-panel="true" onClick={() => onDelete(id)} style={btnStyle('#ef4444')}>✕</button>
            </div>
            <div style={{ color: '#94a3b8', fontSize: 10, marginTop: 3, lineHeight: 1.5 }}>
              {edit.x !== undefined && <span style={{ marginRight: 8 }}>X:{edit.x}px</span>}
              {edit.y !== undefined && <span style={{ marginRight: 8 }}>Y:{edit.y}px</span>}
              {edit.width !== undefined && <span style={{ marginRight: 8 }}>W:{edit.width}px</span>}
              {edit.height !== undefined && <span style={{ marginRight: 8 }}>H:{edit.height}px</span>}
              {edit.fontSize !== undefined && <span style={{ marginRight: 8 }}>Font:{edit.fontSize}px</span>}
              {edit.color && <span style={{ marginRight: 8 }}>Color:{edit.color}</span>}
              {edit.textContent !== undefined && <span>Текст: "{edit.textContent.slice(0, 30)}"</span>}
            </div>
          </div>
        ))}
        {items.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                data-de-panel="true"
                onClick={handleCopyAll}
                style={{ ...btnStyle(copied ? '#059669' : '#7c3aed'), padding: '7px 0', fontSize: 12, fontWeight: 700 }}
              >
                {copied ? '✓ Копирано!' : '📋 Копирай всички'}
              </button>
              <button data-de-panel="true" onClick={onClearAll} style={{ ...btnStyle('#ef4444'), padding: '7px 12px' }}>
                🗑️ Изчисти
              </button>
            </div>
            <button
              data-de-panel="true"
              onClick={handleApplyToCode}
              disabled={applying}
              style={{
                ...btnStyle(applying ? '#374151' : '#0ea5e9'),
                width: '100%', padding: '9px 0', fontSize: 13, fontWeight: 700,
                border: '2px solid #0ea5e9', borderRadius: 6,
                opacity: applying ? 0.7 : 1, cursor: applying ? 'not-allowed' : 'pointer',
                letterSpacing: '0.02em',
              }}
            >
              {applying ? '⏳ Прилагане...' : '🚀 Apply to Code'}
            </button>
            {applyResult && (
              <div style={{
                background: applyResult.success ? 'rgba(5,150,105,0.15)' : 'rgba(239,68,68,0.15)',
                border: `1px solid ${applyResult.success ? '#059669' : '#ef4444'}`,
                borderRadius: 6, padding: '8px 10px',
                color: applyResult.success ? '#10b981' : '#f87171',
                fontSize: 11, lineHeight: 1.5,
              }}>
                {applyResult.message}
                {applyResult.success && (
                  <div style={{ color: '#94a3b8', fontSize: 10, marginTop: 4 }}>
                    Файлът е записан в проекта. Промените са постоянни.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Feedback Panel ───────────────────────────────────────────────────────────

interface FeedbackPanelProps {
  items: FeedbackItem[];
  onDelete: (id: string) => void;
  onClearAll: () => void;
  onClose: () => void;
}

function FeedbackPanel({ items, onDelete, onClearAll, onClose }: FeedbackPanelProps) {
  const { pos, onMouseDown } = useDraggablePanel(
    typeof window !== 'undefined' ? window.innerWidth - 420 : 900,
    80
  );
  const [copied, setCopied] = useState(false);

  const handleCopyAll = () => {
    if (items.length === 0) return;
    const text = items.map((item, i) => {
      const info = item.elementInfo;
      const lines: string[] = [];
      lines.push(`${i + 1}. БЕЛЕЖКА: ${item.note}`);
      lines.push(`   ЕЛЕМЕНТ: ${item.label}`);
      if (info) {
        lines.push(`   Тип: <${info.tag}>${info.id ? ` id="${info.id}"` : ''}${info.classes ? ` class="${info.classes}"` : ''}`);
        if (info.text) lines.push(`   Текст: "${info.text}"`);
        lines.push(`   Selector: ${item.selector}`);
        lines.push(`   Страница: ${info.page}`);
        lines.push(`   Позиция: ${info.position}`);
        if (info.componentName) lines.push(`   Компонент: ${info.componentName}`);
        if (info.filePath) lines.push(`   Файл: ${info.filePath}`);
        const pageComponents = getPageComponents(info.page);
        if (pageComponents.length > 1) {
          lines.push(`   Всички компоненти на страницата:`);
          pageComponents.forEach(c => lines.push(`     - ${c.component} → ${c.file}`));
        }
      } else {
        lines.push(`   Selector: ${item.selector}`);
      }
      return lines.join('\n');
    }).join('\n\n');
    navigator.clipboard.writeText(`=== FEEDBACK БЕЛЕЖКИ (${items.length}) ===\n\n${text}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div data-de-panel="true" style={{
      position: 'fixed', left: pos.x, top: pos.y, zIndex: 2147483646,
      width: 400, background: '#1a1a2e', border: '1px solid #f59e0b',
      borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
      fontFamily: 'system-ui, sans-serif', fontSize: 12, color: '#e2e8f0',
    }}>
      <div onMouseDown={onMouseDown} style={{
        background: 'linear-gradient(90deg,#d97706,#f59e0b)',
        borderRadius: '9px 9px 0 0', padding: '8px 12px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        cursor: 'grab',
      }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>💬 Feedback бележки ({items.length})</span>
        <button data-de-panel="true" onClick={onClose} style={btnStyle('#6b7280')}>✕</button>
      </div>
      <div style={{ padding: 10, maxHeight: 500, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.length === 0 && (
          <div style={{ color: '#6b7280', textAlign: 'center', padding: 20 }}>
            Няма feedback бележки.<br />
            <span style={{ fontSize: 10 }}>Кликни елемент в Feedback режим за да добавиш.</span>
          </div>
        )}
        {items.map(item => (
          <div key={item.id} style={{ background: '#0f0f1a', borderRadius: 6, padding: '8px 10px', border: '1px solid #2d2d4e' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
              <span style={{ color: '#f59e0b', fontSize: 10, fontWeight: 700, flex: 1 }}>{item.label}</span>
              <button data-de-panel="true" onClick={() => onDelete(item.id)} style={btnStyle('#ef4444')}>✕</button>
            </div>
            {item.elementInfo && (
              <div style={{ marginTop: 4, padding: '4px 6px', background: '#1a1a2e', borderRadius: 4, border: '1px solid #2d2d4e' }}>
                <div style={{ color: '#7c3aed', fontSize: 10, fontFamily: 'monospace', lineHeight: 1.6 }}>
                  <span style={{ color: '#94a3b8' }}>Тип: </span>
                  <span style={{ color: '#a78bfa' }}>&lt;{item.elementInfo.tag}&gt;</span>
                  {item.elementInfo.id && <span> <span style={{ color: '#94a3b8' }}>id=</span><span style={{ color: '#10b981' }}>"{item.elementInfo.id}"</span></span>}
                  {item.elementInfo.classes && <span> <span style={{ color: '#94a3b8' }}>class=</span><span style={{ color: '#60a5fa' }}>"{item.elementInfo.classes}"</span></span>}
                </div>
                {item.elementInfo.text && (
                  <div style={{ color: '#94a3b8', fontSize: 10, marginTop: 2 }}>
                    <span>Текст: </span><span style={{ color: '#e2e8f0' }}>"{item.elementInfo.text}"</span>
                  </div>
                )}
                <div style={{ color: '#4b5563', fontSize: 9, marginTop: 2, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.selector}
                </div>
                <div style={{ color: '#4b5563', fontSize: 9, marginTop: 1 }}>
                  📍 {item.elementInfo.position} · {item.elementInfo.page}
                </div>
                {item.elementInfo.componentName && (
                  <div style={{ color: '#7c3aed', fontSize: 9, marginTop: 2 }}>
                    📁 {item.elementInfo.componentName} → {item.elementInfo.filePath}
                  </div>
                )}
              </div>
            )}
            <div style={{ color: '#e2e8f0', marginTop: 6, fontSize: 12, lineHeight: 1.5, background: 'rgba(245,158,11,0.08)', borderRadius: 4, padding: '4px 6px', borderLeft: '2px solid #f59e0b' }}>
              {item.note}
            </div>
            <div style={{ color: '#4b5563', fontSize: 10, marginTop: 4 }}>{item.timestamp}</div>
          </div>
        ))}
        {items.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <button
              data-de-panel="true"
              onClick={handleCopyAll}
              style={{ ...btnStyle(copied ? '#059669' : '#d97706'), flex: 1, padding: '7px 0', fontSize: 12, fontWeight: 700 }}
            >
              {copied ? '✓ Копирано!' : '📋 Копирай всички'}
            </button>
            <button data-de-panel="true" onClick={onClearAll} style={{ ...btnStyle('#ef4444'), padding: '7px 12px' }}>
              🗑️ Изчисти
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Feedback Note Input ──────────────────────────────────────────────────────

interface FeedbackNoteProps {
  targetEl: HTMLElement;
  label: string;
  elementInfo: FeedbackItem['elementInfo'];
  onSave: (note: string) => void;
  onCancel: () => void;
}

function FeedbackNote({ targetEl, label, elementInfo, onSave, onCancel }: FeedbackNoteProps) {
  const [note, setNote] = useState('');
  const rect = targetEl.getBoundingClientRect();
  const left = Math.min(rect.left, window.innerWidth - 340);
  const top = rect.bottom + 8 > window.innerHeight - 260 ? rect.top - 260 : rect.bottom + 8;

  return (
    <div data-de-panel="true" style={{
      position: 'fixed', left, top, zIndex: 2147483647,
      width: 320, background: '#1a1a2e', border: '2px solid #f59e0b',
      borderRadius: 8, padding: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.7)',
      fontFamily: 'system-ui, sans-serif',
    }}>
      <div style={{ color: '#f59e0b', fontSize: 11, fontWeight: 700, marginBottom: 6 }}>
        💬 Бележка за елемент
      </div>
      {/* Element info block */}
      {elementInfo && (
        <div style={{ background: '#0f0f1a', borderRadius: 4, padding: '6px 8px', marginBottom: 8, border: '1px solid #2d2d4e' }}>
          <div style={{ color: '#a78bfa', fontSize: 10, fontFamily: 'monospace', lineHeight: 1.7 }}>
            <span style={{ color: '#94a3b8' }}>Тип: </span>
            <span style={{ color: '#a78bfa' }}>&lt;{elementInfo.tag}&gt;</span>
            {elementInfo.id && <span> <span style={{ color: '#94a3b8' }}>id=</span><span style={{ color: '#10b981' }}>"{elementInfo.id}"</span></span>}
            {elementInfo.classes && (
              <div><span style={{ color: '#94a3b8' }}>class: </span><span style={{ color: '#60a5fa' }}>{elementInfo.classes}</span></div>
            )}
            {elementInfo.text && (
              <div><span style={{ color: '#94a3b8' }}>Текст: </span><span style={{ color: '#e2e8f0' }}>"{elementInfo.text}"</span></div>
            )}
            <div style={{ color: '#4b5563', fontSize: 9, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </div>
            <div style={{ color: '#4b5563', fontSize: 9 }}>
              📍 {elementInfo.position} · {elementInfo.page}
            </div>
            {elementInfo.componentName && (
              <div style={{ color: '#7c3aed', fontSize: 9, marginTop: 2 }}>
                📁 {elementInfo.componentName} → {elementInfo.filePath}
              </div>
            )}
          </div>
        </div>
      )}
      <textarea
        data-de-panel="true"
        autoFocus
        value={note}
        onChange={e => setNote(e.target.value)}
        placeholder="Опиши какво трябва да се промени..."
        style={{
          width: '100%', minHeight: 80, background: '#0f0f1a',
          color: '#e2e8f0', border: '1px solid #2d2d4e', borderRadius: 4,
          padding: '6px 8px', fontSize: 12, resize: 'vertical', outline: 'none',
          fontFamily: 'inherit', boxSizing: 'border-box',
        }}
        onKeyDown={e => {
          if (e.key === 'Escape') onCancel();
        }}
      />
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button data-de-panel="true" onClick={() => { if (note.trim()) onSave(note.trim()); }} style={{ ...btnStyle('#d97706'), flex: 1, padding: '6px 0' }}>
          💾 Запази бележката
        </button>
        <button data-de-panel="true" onClick={onCancel} style={btnStyle('#6b7280')}>✕</button>
      </div>
    </div>
  );
}

// ─── Main DesignEditor Component ──────────────────────────────────────────────

type EditorMode = 'off' | 'design' | 'feedback';

export default function DesignEditor() {
  const [mode, setMode] = useState<EditorMode>('off');
  const [store, setStore] = useState<DesignEditorStore>({ edits: {}, feedback: [], version: 2 });

  // Design mode state
  const [selectedEl, setSelectedEl] = useState<HTMLElement | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingTextEl, setEditingTextEl] = useState<HTMLElement | null>(null);

  // Feedback mode state
  const [feedbackTargetEl, setFeedbackTargetEl] = useState<HTMLElement | null>(null);
  const [feedbackLabel, setFeedbackLabel] = useState('');
  const [showFeedbackPanel, setShowFeedbackPanel] = useState(false);

  // Saved edits mode state
  const [showSavedEditsPanel, setShowSavedEditsPanel] = useState(false);

  // Layer hierarchy panel state
  const [showLayerPanel, setShowLayerPanel] = useState(false);

  // Quick style presets panel state
  const [showPresetsPanel, setShowPresetsPanel] = useState(false);

  // Pending changes (not yet saved)
  const pendingEdits = useRef<Record<string, PositionedEdit>>({});

  // Undo history stack — each entry is a snapshot of pendingEdits BEFORE a change
  const undoHistory = useRef<Array<Record<string, PositionedEdit>>>([]);

  // Refs to always have latest values in event handlers (avoids stale closures)
  const modeRef = useRef<EditorMode>('off');
  const selectedIdRef = useRef<string | null>(null);
  const selectedElRef = useRef<HTMLElement | null>(null);
  const storeRef = useRef<DesignEditorStore>({ edits: {}, feedback: [], version: 2 });

  // Stable ref for the delete function — set once on mount, reads only refs so never stale
  const deleteElementByIdRef = useRef<((id: string) => void) | null>(null);

  // Keep refs in sync with state
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  useEffect(() => { selectedElRef.current = selectedEl; }, [selectedEl]);
  useEffect(() => { storeRef.current = store; }, [store]);

  // Load store on mount
  useEffect(() => {
    const s = loadStore();

    // ── Revert: remove 11 Design Editor changes (requested 2026-04-26) ──
    const REVERTED_SELECTORS = [
      'div:nth-of-type(1) > div:nth-of-type(4) > div:nth-of-type(1) > img',
      'div:nth-of-type(1) > div:nth-of-type(4) > div:nth-of-type(2) > h1 > span',
      'div:nth-of-type(1) > div:nth-of-type(4)',
      'div:nth-of-type(1)',
      'div:nth-of-type(1) > div:nth-of-type(4) > div:nth-of-type(4) > div:nth-of-type(2)',
      'div:nth-of-type(1) > div:nth-of-type(4) > div:nth-of-type(2) > h1',
      'div:nth-of-type(1) > div:nth-of-type(4) > div:nth-of-type(4) > div:nth-of-type(1) > div:nth-of-type(2) > div',
      'div:nth-of-type(1) > div:nth-of-type(4) > div:nth-of-type(4) > div:nth-of-type(1) > div:nth-of-type(1)',
      'div:nth-of-type(1) > div:nth-of-type(4)',
      'div:nth-of-type(1)',
    ];
    let changed = false;
    REVERTED_SELECTORS.forEach(sel => {
      if (s.edits[sel]) {
        try {
          const el = document.querySelector(sel) as HTMLElement | null;
          if (el) el.style.cssText = '';
        } catch {}
        delete s.edits[sel];
        changed = true;
      }
    });
    if (changed) saveStore(s);
    // ── End revert ──

    setStore(s);
    storeRef.current = s;
    pendingEdits.current = { ...s.edits };
  }, []);

  // Keyboard shortcuts: registered ONCE, reads latest values via refs
  useEffect(() => {
    // Make body focusable so keydown events are reliably received
    if (!document.body.hasAttribute('tabindex')) {
      document.body.setAttribute('tabindex', '-1');
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only active when editor is on
      if (modeRef.current === 'off') return;

      // Ignore if typing in an input/textarea/select/contenteditable
      const target = e.target as HTMLElement;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (target?.isContentEditable) return;

      const currentSelectedId = selectedIdRef.current;
      const currentSelectedEl = selectedElRef.current;
      const currentStore = storeRef.current;

      // Ctrl+Z — undo last change
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (undoHistory.current.length === 0) return;
        // Capture selectors currently in pendingEdits BEFORE restoring previous state
        const selectorsBeforeUndo = Object.keys(pendingEdits.current);
        const previous = undoHistory.current.pop()!;
        const previousKeys = Object.keys(previous);
        // Reset inline styles for elements that existed before undo but not in previous state
        selectorsBeforeUndo.forEach(sel => {
          if (!previousKeys.includes(sel)) {
            try {
              const el = document.querySelector(sel) as HTMLElement | null;
              if (el) el.style.cssText = '';
            } catch {}
          }
        });
        pendingEdits.current = { ...previous };
        // Re-apply previous state to DOM
        Object.values(previous).forEach(edit => {
          try {
            const el = document.querySelector(edit.selector) as HTMLElement | null;
            if (el) applyEditToElement(el, edit);
          } catch {}
        });
        const next = { ...currentStore, edits: { ...pendingEdits.current } };
        saveStore(next);
        setStore(next);
        storeRef.current = next;
        setSelectedId(null);
        setSelectedEl(null);
        return;
      }

      // Delete — remove selected element's edits
      if (e.key === 'Delete') {
        e.preventDefault();
        e.stopPropagation();
        const id = selectedIdRef.current;
        if (id && deleteElementByIdRef.current) {
          deleteElementByIdRef.current(id);
        }
        return;
      }

      // Arrow keys — nudge selected element position
      if (currentSelectedId && currentSelectedEl && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        let dx = 0;
        let dy = 0;
        if (e.key === 'ArrowLeft')  dx = -step;
        if (e.key === 'ArrowRight') dx = +step;
        if (e.key === 'ArrowUp')    dy = -step;
        if (e.key === 'ArrowDown')  dy = +step;

        // Push snapshot before nudge
        undoHistory.current.push({ ...pendingEdits.current });
        let current = pendingEdits.current[currentSelectedId] || { selector: currentSelectedId, label: '' };
        let newX = (current.x ?? 0) + dx;
        let newY = (current.y ?? 0) + dy;
        const updated = { ...current, x: newX, y: newY };
        pendingEdits.current[currentSelectedId] = updated;
        currentSelectedEl.style.position = 'relative';
        currentSelectedEl.style.left = `${newX}px`;
        currentSelectedEl.style.top = `${newY}px`;
        return;
      }
    };

    // Use capture phase so we get the event before anything else
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, []); // Empty deps — refs keep values fresh

  // Apply all edits on load
  useEffect(() => {
    if (Object.keys(store.edits).length === 0) return;
    const t = setTimeout(() => {
      Object.values(store.edits).forEach(edit => {
        try {
          const el = document.querySelector(edit.selector) as HTMLElement | null;
          if (el) applyEditToElement(el, edit);
        } catch {}
      });
    }, 150);
    return () => clearTimeout(t);
  }, [store.edits]);

  // Click capture
  useEffect(() => {
    if (mode === 'off') return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-de-panel]')) return;

      e.preventDefault();
      e.stopPropagation();

      if (mode === 'design') {
        const selector = buildSelector(target);
        const label = getLabel(target);
        const existing = pendingEdits.current[selector];
        if (!existing) {
          pendingEdits.current[selector] = { selector, label };
        }
        setSelectedEl(target);
        setSelectedId(selector);
        selectedElRef.current = target;
        selectedIdRef.current = selector;
        setEditingTextEl(null);
        // Ensure document body has focus so keyboard events are received
        if (document.activeElement && document.activeElement !== document.body) {
          (document.activeElement as HTMLElement).blur();
        }
        document.body.focus();
      } else if (mode === 'feedback') {
        setFeedbackTargetEl(target);
        setFeedbackLabel(getLabel(target));
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [mode]);

  // Cursor
  useEffect(() => {
    if (mode !== 'off') {
      document.body.style.cursor = 'crosshair';
    } else {
      document.body.style.cursor = '';
      setSelectedEl(null);
      setSelectedId(null);
      setEditingTextEl(null);
      setFeedbackTargetEl(null);
    }
  }, [mode]);

  // ── Design mode: drag to move (no undo push on every move — push on drag end) ──
  const handleDragMove = useCallback((dx: number, dy: number) => {
    if (!selectedEl || !selectedId) return;
    let current = pendingEdits.current[selectedId] || { selector: selectedId, label: '' };
    let newX = (current.x ?? 0) + dx;
    let newY = (current.y ?? 0) + dy;
    pendingEdits.current[selectedId] = { ...current, x: newX, y: newY };
    selectedEl.style.position = 'relative';
    selectedEl.style.left = `${newX}px`;
    selectedEl.style.top = `${newY}px`;
  }, [selectedEl, selectedId]);

  // ── Push undo snapshot once drag ends ──
  const handleDragEnd = useCallback(() => {
    undoHistory.current.push({ ...pendingEdits.current });
    // Auto-save after drag
    const next = { ...storeRef.current, edits: { ...pendingEdits.current } };
    saveStore(next);
    storeRef.current = next;
    setStore(next);
  }, []);

  // ── Design mode: resize (no undo push on every move — push on resize end) ──
  const handleResizeMove = useCallback((handle: ResizeHandle, dx: number, dy: number) => {
    if (!selectedEl || !selectedId) return;
    let current = pendingEdits.current[selectedId] || { selector: selectedId, label: '' };
    const rect = selectedEl.getBoundingClientRect();
    let newW = current.width ?? rect.width;
    let newH = current.height ?? rect.height;
    let newX = current.x ?? 0;
    let newY = current.y ?? 0;

    if (handle.includes('e')) newW += dx;
    if (handle.includes('s')) newH += dy;
    if (handle.includes('w')) { newW -= dx; newX += dx; }
    if (handle.includes('n')) { newH -= dy; newY += dy; }

    newW = Math.max(20, newW);
    newH = Math.max(10, newH);

    pendingEdits.current[selectedId] = { ...current, width: newW, height: newH, x: newX, y: newY };
    selectedEl.style.width = `${newW}px`;
    selectedEl.style.height = `${newH}px`;
    selectedEl.style.position = 'relative';
    selectedEl.style.left = `${newX}px`;
    selectedEl.style.top = `${newY}px`;
  }, [selectedEl, selectedId]);

  // ── Push undo snapshot once resize ends ──
  const handleResizeEnd = useCallback(() => {
    undoHistory.current.push({ ...pendingEdits.current });
    // Auto-save after resize
    const next = { ...storeRef.current, edits: { ...pendingEdits.current } };
    saveStore(next);
    storeRef.current = next;
    setStore(next);
  }, []);

  // ── Inspector change ──
  const handleInspectorChange = useCallback((patch: Partial<PositionedEdit>) => {
    if (!selectedEl || !selectedId) return;
    // Push to undo history before applying change
    undoHistory.current.push({ ...pendingEdits.current });
    let current = pendingEdits.current[selectedId] || { selector: selectedId, label: '' };
    const updated = { ...current, ...patch };
    pendingEdits.current[selectedId] = updated;
    applyEditToElement(selectedEl, updated);
    // Auto-save after inspector change
    const next = { ...storeRef.current, edits: { ...pendingEdits.current } };
    saveStore(next);
    storeRef.current = next;
    setStore(next);
  }, [selectedEl, selectedId]);

  // ── Save all pending changes ──
  const handleSave = useCallback(() => {
    const next: DesignEditorStore = {
      ...store,
      edits: { ...pendingEdits.current },
    };
    saveStore(next);
    setStore(next);
    // Flash confirmation
    const btn = document.getElementById('de-save-btn');
    if (btn) {
      btn.textContent = '✓ Запазено!';
      setTimeout(() => { if (btn) btn.textContent = '💾 Save'; }, 1500);
    }
  }, [store]);

  // ── Inline text save ──
  const handleTextSave = useCallback((text: string) => {
    if (!editingTextEl || !selectedId) return;
    // Push to undo history before applying change
    undoHistory.current.push({ ...pendingEdits.current });
    let current = pendingEdits.current[selectedId] || { selector: selectedId, label: '' };
    const updated = { ...current, textContent: text };
    pendingEdits.current[selectedId] = updated;
    if (editingTextEl.childElementCount === 0) editingTextEl.textContent = text;
    // Auto-save after text edit
    const next = { ...storeRef.current, edits: { ...pendingEdits.current } };
    saveStore(next);
    storeRef.current = next;
    setStore(next);
    setEditingTextEl(null);
  }, [editingTextEl, selectedId]);

  // ── Delete edit — stable function set once on mount, reads only refs ──
  useEffect(() => {
    deleteElementByIdRef.current = (id: string) => {
      // Push current state to undo history before deleting
      undoHistory.current.push({ ...pendingEdits.current });
      // Find the element and reset its inline styles
      const edit = pendingEdits.current[id];
      if (edit) {
        try {
          const el = document.querySelector(edit.selector) as HTMLElement | null;
          if (el) {
            el.style.cssText = '';
          }
        } catch {}
      }
      delete pendingEdits.current[id];
      const next = { ...storeRef.current, edits: { ...pendingEdits.current } };
      saveStore(next);
      storeRef.current = next;
      setStore({ ...next });
      setSelectedId(null);
      setSelectedEl(null);
      selectedIdRef.current = null;
      selectedElRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — reads only refs, never stale

  const handleDeleteEdit = useCallback((id: string) => {
    if (deleteElementByIdRef.current) deleteElementByIdRef.current(id);
  }, []);

  const handleDeleteSavedEdit = useCallback((id: string) => {
    if (deleteElementByIdRef.current) deleteElementByIdRef.current(id);
  }, []);

  const handleClearAllEdits = useCallback(() => {
    pendingEdits.current = {};
    const next = { ...store, edits: {} };
    saveStore(next);
    setStore(next);
    setSelectedId(null);
    setSelectedEl(null);
  }, [store]);

  // ── Feedback save ──
  const handleFeedbackSave = useCallback((note: string) => {
    if (!feedbackTargetEl) return;
    const page = window.location.pathname;
    const pageComponents = getPageComponents(page);
    // Best-guess component: first non-Sidebar entry
    const primaryComponent = pageComponents.find(c => c.component !== 'Sidebar') || pageComponents[0];
    const item: FeedbackItem = {
      id: `fb-${Date.now()}`,
      selector: buildSelector(feedbackTargetEl),
      label: feedbackLabel,
      note,
      timestamp: new Date().toLocaleString('bg-BG'),
      elementInfo: {
        tag: feedbackTargetEl.tagName.toLowerCase(),
        id: feedbackTargetEl.id || '',
        classes: Array.from(feedbackTargetEl.classList).join(' '),
        text: feedbackTargetEl.textContent?.trim().slice(0, 40) || '',
        page,
        position: `${Math.round(feedbackTargetEl.getBoundingClientRect().left)}px, ${Math.round(feedbackTargetEl.getBoundingClientRect().top)}px`,
        componentName: primaryComponent?.component || '',
        filePath: primaryComponent?.file || '',
      },
    };
    const next = { ...store, feedback: [...(store.feedback || []), item] };
    saveStore(next);
    setStore(next);
    setFeedbackTargetEl(null);
  }, [feedbackTargetEl, feedbackLabel, store]);

  const handleDeleteFeedback = useCallback((id: string) => {
    const next = { ...store, feedback: store.feedback.filter(f => f.id !== id) };
    saveStore(next);
    setStore(next);
  }, [store]);

  const handleClearAllFeedback = useCallback(() => {
    const next = { ...store, feedback: [] };
    saveStore(next);
    setStore(next);
  }, [store]);

  // ── Layer panel: select element by selector ──
  const handleSelectLayer = useCallback((selector: string) => {
    try {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (el) {
        setSelectedEl(el);
        setSelectedId(selector);
        selectedElRef.current = el;
        selectedIdRef.current = selector;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } catch {}
  }, []);

  // ── Toolbar ──
  const toolbarStyle: React.CSSProperties = {
    position: 'fixed', bottom: 20, right: 20,
    zIndex: 2147483647,
    background: '#1a1a2e',
    border: '1px solid #7c3aed',
    borderRadius: 12,
    padding: '8px 14px',
    display: 'flex', alignItems: 'center', gap: 8,
    boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
    fontFamily: 'system-ui, sans-serif',
    fontSize: 12,
    userSelect: 'none',
    whiteSpace: 'nowrap',
  };

  const modeBtn = (label: string, active: boolean, onClick: () => void, color: string): React.CSSProperties => ({
    background: active ? color : '#2d2d4e',
    border: `1px solid ${active ? color : '#3d3d5e'}`,
    borderRadius: 6, color: '#fff',
    padding: '5px 10px', cursor: 'pointer', fontSize: 11, fontWeight: active ? 700 : 400,
  });

  return (
    <>
      {/* Toolbar */}
      <div data-de-panel="true" style={toolbarStyle}>
        <span style={{ color: '#7c3aed', fontWeight: 700, fontSize: 13, marginRight: 4 }}>✏️</span>

        <button
          data-de-panel="true"
          style={modeBtn('Design', mode === 'design', () => {}, '#7c3aed')}
          onClick={() => setMode(m => m === 'design' ? 'off' : 'design')}
        >
          {mode === 'design' ? '● Design ON' : 'Design'}
        </button>

        <button
          data-de-panel="true"
          style={modeBtn('Feedback', mode === 'feedback', () => {}, '#d97706')}
          onClick={() => setMode(m => m === 'feedback' ? 'off' : 'feedback')}
        >
          {mode === 'feedback' ? '● Feedback ON' : 'Feedback'}
        </button>

        {mode === 'design' && (
          <>
            <button
              data-de-panel="true"
              id="de-save-btn"
              onClick={handleSave}
              style={{ ...btnStyle('#059669'), padding: '5px 12px', fontSize: 11, fontWeight: 700 }}
            >
              💾 Save
            </button>
            <button
              data-de-panel="true"
              onClick={() => setShowSavedEditsPanel(v => !v)}
              style={{ ...btnStyle('#7c3aed'), padding: '5px 10px', fontSize: 11 }}
            >
              📋 Промени ({Object.keys(store.edits).length})
            </button>
            <button
              data-de-panel="true"
              onClick={() => setShowLayerPanel(v => !v)}
              style={{ ...btnStyle(showLayerPanel ? '#0ea5e9' : '#2d2d4e'), padding: '5px 10px', fontSize: 11, border: `1px solid ${showLayerPanel ? '#0ea5e9' : '#3d3d5e'}` }}
            >
              🗂 Слоеве
            </button>
            <button
              data-de-panel="true"
              onClick={() => setShowPresetsPanel(v => !v)}
              style={{ ...btnStyle(showPresetsPanel ? '#10b981' : '#2d2d4e'), padding: '5px 10px', fontSize: 11, border: `1px solid ${showPresetsPanel ? '#10b981' : '#3d3d5e'}` }}
            >
              ⚡ Стилове
            </button>
          </>
        )}

        {mode === 'feedback' && (
          <button
            data-de-panel="true"
            onClick={() => setShowFeedbackPanel(v => !v)}
            style={{ ...btnStyle('#d97706'), padding: '5px 10px', fontSize: 11 }}
          >
            💬 Бележки ({(store.feedback || []).length})
          </button>
        )}

        {mode !== 'off' && (
          <span style={{ color: '#4b5563', fontSize: 10, marginLeft: 4 }}>
            {mode === 'design' ?'Клик = избери · Drag = мести · Ctrl+Z = undo · Del = изтрий · ↑↓←→ = 1px' :'Клик = добави бележка'}
          </span>
        )}
      </div>

      {/* Selection overlay */}
      {mode === 'design' && selectedEl && (
        <SelectionOverlay
          targetEl={selectedEl}
          onDragMove={handleDragMove}
          onResizeMove={handleResizeMove}
          onDragEnd={handleDragEnd}
          onResizeEnd={handleResizeEnd}
          onDoubleClick={() => {
            if (selectedEl && selectedEl.childElementCount === 0) {
              setEditingTextEl(selectedEl);
            }
          }}
        />
      )}

      {/* Inspector */}
      {mode === 'design' && selectedId && pendingEdits.current[selectedId] && (
        <Inspector
          edit={pendingEdits.current[selectedId]}
          onChange={handleInspectorChange}
          onClose={() => { setSelectedEl(null); setSelectedId(null); }}
          onDelete={() => handleDeleteEdit(selectedId)}
          onEditText={() => {
            if (selectedEl && selectedEl.childElementCount === 0) {
              setEditingTextEl(selectedEl);
            }
          }}
        />
      )}

      {/* Inline text editor */}
      {editingTextEl && selectedId && (
        <InlineTextEditor
          targetEl={editingTextEl}
          initialText={pendingEdits.current[selectedId]?.textContent ?? editingTextEl.textContent ?? ''}
          onSave={handleTextSave}
          onCancel={() => setEditingTextEl(null)}
        />
      )}

      {/* Saved edits panel */}
      {showSavedEditsPanel && (
        <SavedEditsPanel
          edits={store.edits}
          onDelete={handleDeleteSavedEdit}
          onClearAll={handleClearAllEdits}
          onClose={() => setShowSavedEditsPanel(false)}
        />
      )}

      {/* Layer hierarchy panel */}
      {showLayerPanel && mode === 'design' && (
        <LayerHierarchyPanel
          edits={store.edits}
          selectedId={selectedId}
          onSelectLayer={handleSelectLayer}
          onDeleteLayer={handleDeleteEdit}
          onClose={() => setShowLayerPanel(false)}
        />
      )}

      {/* Quick style presets panel */}
      {showPresetsPanel && mode === 'design' && (
        <QuickStylePresetsPanel
          selectedId={selectedId}
          selectedEl={selectedEl}
          onApplyPreset={handleInspectorChange}
          onClose={() => setShowPresetsPanel(false)}
        />
      )}

      {/* Feedback note input */}
      {mode === 'feedback' && feedbackTargetEl && (
        <FeedbackNote
          targetEl={feedbackTargetEl}
          label={feedbackLabel}
          elementInfo={(() => {
            const page = window.location.pathname;
            const pageComponents = getPageComponents(page);
            const primaryComponent = pageComponents.find(c => c.component !== 'Sidebar') || pageComponents[0];
            return {
              tag: feedbackTargetEl.tagName.toLowerCase(),
              id: feedbackTargetEl.id || '',
              classes: Array.from(feedbackTargetEl.classList).join(' '),
              text: feedbackTargetEl.textContent?.trim().slice(0, 40) || '',
              page,
              position: `${Math.round(feedbackTargetEl.getBoundingClientRect().left)}px, ${Math.round(feedbackTargetEl.getBoundingClientRect().top)}px`,
              componentName: primaryComponent?.component || '',
              filePath: primaryComponent?.file || '',
            };
          })()}
          onSave={handleFeedbackSave}
          onCancel={() => setFeedbackTargetEl(null)}
        />
      )}

      {/* Feedback panel */}
      {showFeedbackPanel && (
        <FeedbackPanel
          items={store.feedback || []}
          onDelete={handleDeleteFeedback}
          onClearAll={handleClearAllFeedback}
          onClose={() => setShowFeedbackPanel(false)}
        />
      )}
    </>
  );
}
